import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type { ITheme } from '@inter-ikea-kompis/types';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import IconPill from '../icon-pill/IconPill.js';
import IconPillGroupStyle from './IconPillGroupStyle.js';

/**
 * Icon Pill class.
 */

export default class IconPillGroup extends KompisElement {
	// Label for the icon pill group to set as an aria-label for accessibility reasons.
	@property({ type: String })
	public override ariaLabel: string | null = null;

	// Theme.
	@property({ type: Object, required: true })
	public theme!: ITheme;

	/**
	 * Renders the component.
	 *
	 * @returns Template.
	 */
	public override render(): RenderResult {
		return html`
			<style>
				${IconPillGroupStyle.getStyle({
					theme: this.theme
				})}
			</style>
			<div role="group" aria-label="${this.ariaLabel || ''}" class="container">
				<slot @keydown="${(event: KeyboardEvent) => this.onKeyDown(event)}"></slot>
			</div>
		`;
	}

	/**
	 * When key down.
	 *
	 * @param event Keyboard event.
	 */
	private onKeyDown(event: KeyboardEvent): void {
		switch (event.key) {
			case 'ArrowRight':
			case 'ArrowDown':
				event.preventDefault();
				this.changeButtonFocus(+1);
				break;
			case 'ArrowLeft':
			case 'ArrowUp':
				event.preventDefault();
				this.changeButtonFocus(-1);
				break;
		}
	}

	private changeButtonFocus(increment: number): void {
		const iconPills = Array.from(this.children)
			.filter((child): child is IconPill => child instanceof IconPill)
			.filter((node) => !node.disabled);
		const currentlyFocusedIconPill = iconPills.find((element) => element.matches(':focus-within'));
		if (!currentlyFocusedIconPill) {
			return;
		}
		const index = iconPills.indexOf(currentlyFocusedIconPill);
		const iconPill = iconPills[(iconPills.length + index + increment) % iconPills.length];
		iconPill.shadowRoot?.querySelector('div')?.focus();
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'kompis-icon-pill-group': IconPillGroup;
	}
}
